import { Link } from "react-router-dom";
import { ArrowRight, Quote } from "lucide-react";
import Reveal from "@/components/Reveal";
import SEOHead from "@/components/SEOHead";
import FaqSection from "@/components/FaqSection";
import { ABOUT_PHOTO, ABOUT_STRIP_PHOTOS, photoDims } from "@/lib/photos";
import { ABOUT_FAQS } from "@/lib/faqs";

export default function About() {
 return (
 <div data-testid="about-page">
 <SEOHead
 title="About Harris Homes | Tahlequah Home Builder"
 description="Meet the builder behind Harris Homes. One local team builds your custom home in Tahlequah, OK, from first sketch to final walk-through."
 canonical="https://harrishomesok.com/about"
 />
 {/* Page header */}
 <section className="pt-20 md:pt-32 pb-16 md:pb-24 px-6 md:px-10">
 <div className="max-w-[1480px] mx-auto">
 <Reveal>
 <div className="overline mb-6">About Harris Homes</div>
 <h1 className="font-display text-5xl sm:text-7xl md:text-[7.5rem] leading-[0.95] tracking-tight max-w-5xl">
 A builder
 <br />
 <span style={{ fontStyle: "italic", color: "var(--hh-gold)" }}>
 with a name on it.
 </span>
 </h1>
 </Reveal>
 </div>
 </section>

 {/* Story */}
 <section className="px-6 md:px-10 pb-24 md:pb-32">
 <div className="max-w-[1480px] mx-auto grid md:grid-cols-12 gap-12 md:gap-16">
 <Reveal className="md:col-span-5">
 <div className="relative img-zoom">
 <img
 src={ABOUT_PHOTO}
 alt="Home addition project with custom exterior design and quality craftsmanship by Harris Homes"
 width={photoDims(ABOUT_PHOTO)?.w}
 height={photoDims(ABOUT_PHOTO)?.h}
 className="w-full h-[620px] object-cover"
 loading="lazy"
 />
 </div>
 </Reveal>
 <Reveal delay={0.1} className="md:col-span-7 md:pt-10">
 <div className="overline mb-5">Our Story</div>
 <h2 className="font-display text-3xl md:text-5xl leading-[1.1] tracking-tight max-w-xl">
 Family-run. Tahlequah-rooted.
 <span style={{ fontStyle: "italic", color: "var(--hh-gold)" }}>
 {" "}Built for the long haul.
 </span>
 </h2>
 <div className="gold-rule w-24 my-8" />
 <div
 className="space-y-6 text-base md:text-lg leading-relaxed max-w-xl"
 style={{ color: "var(--hh-text-dim)" }}
 >
 <p>
 Harris Homes LLC is a custom residential builder based in
 Tahlequah, Oklahoma, led by builder Kolin Harris. We build
 new homes, remodel the ones people already love, and add
 the rooms families have been waiting for, all with the
 same hands-on, hands-dirty approach.
 </p>
 <p>
 We're small on purpose. Every project gets direct attention
 from Kolin, the builder whose name is on the door. That means
 honest conversations, real budgets, careful schedules, and
 a finished house we'd be proud to put our family in.
 </p>
 <p>
 We work with local trades we know and trust, and we treat
 every job, a kitchen remodel or a ground-up build, like
 the most important one on the calendar. Because for the
 person we're building it for, it is.
 </p>
 </div>
 </Reveal>
 </div>
 </section>

 {/* Pull quote */}
 <section
 className="py-24 md:py-32 px-6 md:px-10 relative"
 style={{ background: "var(--hh-bg-2)" }}
 >
 <div className="max-w-[1100px] mx-auto text-center">
 <Reveal>
 <Quote
 size={36}
 strokeWidth={1}
 style={{ color: "var(--hh-gold)" }}
 className="mx-auto mb-6"
 />
 <p
 className="font-display text-3xl md:text-5xl leading-[1.2] tracking-tight max-w-3xl mx-auto"
 style={{ color: "var(--hh-text)" }}
 >
 "We don't just build houses, we build the place
 <span style={{ fontStyle: "italic", color: "var(--hh-gold)" }}>
 {" "}your story actually happens in."
 </span>
 </p>
 <div
 className="mt-8 text-[11px] tracking-[0.4em] uppercase"
 style={{ color: "var(--hh-gold)", fontFamily: "Marcellus, serif" }}
 >
 Kolin Harris · Harris Homes LLC
 </div>
 </Reveal>
 </div>
 </section>

 {/* Values */}
 <section className="py-24 md:py-32 px-6 md:px-10">
 <div className="max-w-[1480px] mx-auto">
 <Reveal>
 <div className="overline mb-6">What We Stand For</div>
 <h2 className="font-display text-4xl md:text-6xl leading-[1.05] tracking-tight max-w-3xl">
 Four things we won't bend on.
 </h2>
 </Reveal>
 <div className="mt-16 grid md:grid-cols-2 gap-12 md:gap-16">
 {[
 {
 n: "01",
 title: "Real Craftsmanship",
 body: "We measure twice. We sweat the details that won't be photographed but you'll touch every day. The corners, the reveals, the transitions, that's where a home tells the truth.",
 },
 {
 n: "02",
 title: "Honest Communication",
 body: "You'll always know where the job stands, what's coming next, and what it really costs. No surprises. No hiding behind change orders.",
 },
 {
 n: "03",
 title: "Quality Materials",
 body: "We spec materials we'd put in our own home and source from suppliers we've vetted personally, so the home performs as well in year twenty as it did at the walkthrough.",
 },
 {
 n: "04",
 title: "Local Accountability",
 body: "We live and work in the same community we build in. You'll see us at the diner, the ball field, and the lumber yard, and we'd rather lose a job than lose a neighbor's trust.",
 },
 ].map((v, i) => (
 <Reveal key={v.n} delay={i * 0.08}>
 <div data-testid={`about-value-${v.n}`} className="grid grid-cols-12 gap-6">
 <div className="col-span-2">
 <div
 className="font-display text-5xl md:text-6xl"
 style={{ color: "var(--hh-gold)" }}
 >
 {v.n}
 </div>
 </div>
 <div className="col-span-10">
 <h3 className="font-display text-2xl md:text-3xl mb-3 leading-tight">
 {v.title}
 </h3>
 <p className="text-sm md:text-base leading-relaxed" style={{ color: "var(--hh-text-dim)" }}>
 {v.body}
 </p>
 </div>
 </div>
 </Reveal>
 ))}
 </div>
 </div>
 </section>

 {/* Photo strip */}
 <section className="py-16 md:py-20 px-6 md:px-10">
 <div className="max-w-[1480px] mx-auto grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
 {ABOUT_STRIP_PHOTOS.map((src, i) => {
 const labels = [
 "Exterior · Custom Home",
 "Open-Plan Living",
 "Custom Kitchen",
 "Built-In Detail",
 ];
 return (
 <Reveal key={src} delay={i * 0.08}>
 <div className="relative img-zoom h-[260px] md:h-[320px] group">
 <img src={src} alt={labels[i]} width={photoDims(src)?.w} height={photoDims(src)?.h} className="w-full h-full object-cover" loading="lazy" />
 <div className="absolute inset-x-0 bottom-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity"
 style={{ background: "linear-gradient(180deg, transparent, rgba(10,9,8,0.85))" }}>
 <span className="text-[10px] tracking-[0.32em] uppercase" style={{ color: "var(--hh-gold-light)", fontFamily: "Marcellus, serif" }}>
 {labels[i]}
 </span>
 </div>
 </div>
 </Reveal>
 );
 })}
 </div>
 </section>

 {/* FAQ */}
 <FaqSection
 overline="Frequently Asked"
 headingLead="More about"
 headingItalic="Harris Homes."
 items={ABOUT_FAQS}
 testId="about-faq"
 />

 {/* CTA */}
 <section className="py-24 md:py-32 px-6 md:px-10 text-center">
 <Reveal>
 <h2 className="font-display text-4xl md:text-6xl leading-tight tracking-tight max-w-3xl mx-auto">
 Let's talk about
 <span style={{ fontStyle: "italic", color: "var(--hh-gold)" }}>
 {" "}your next home.
 </span>
 </h2>
 <div className="mt-10">
 <Link to="/contact" data-testid="about-contact-cta" className="btn-gold">
 Get In Touch <ArrowRight size={14} />
 </Link>
 </div>
 </Reveal>
 </section>
 </div>
 );
}
